import { useState, useEffect, useCallback } from 'react';
import { 
  Play, Pause, RefreshCw, Settings, AlertTriangle, 
  CheckCircle, Clock, TrendingUp, List, Power
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import { getBranchAuthHeaders } from '@/utils/branchApi';

const API = process.env.REACT_APP_BACKEND_URL;

const throttleHeaders = () => ({
  'Content-Type': 'application/json',
  ...getBranchAuthHeaders()
});

export default function OrderThrottlePage() {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});

  const headers = throttleHeaders();

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/kitchen/throttle/status`, { headers: throttleHeaders() });
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        setSettingsForm(data.settings || {});
      }
    } catch (err) {
      console.error('Failed to fetch throttle status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/kitchen/throttle/events?limit=20`, { headers: throttleHeaders() });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.events || []);
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchEvents();
    
    // Poll every 30 seconds
    const interval = setInterval(() => {
      fetchStatus();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchStatus, fetchEvents]);

  const handlePause = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/kitchen/throttle/pause`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ reason: 'manual' })
      });
      
      if (res.ok) {
        const data = await res.json();
        setStatus(data.status);
        toast.success('Online orders paused');
        fetchEvents();
      } else {
        toast.error('Failed to pause orders');
      }
    } catch (err) {
      toast.error('Error pausing orders');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResume = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/kitchen/throttle/resume`, {
        method: 'POST',
        headers
      });
      
      if (res.ok) {
        const data = await res.json();
        setStatus(data.status);
        toast.success('Online orders resumed');
        fetchEvents();
      } else {
        toast.error('Failed to resume orders');
      }
    } catch (err) {
      toast.error('Error resuming orders');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setActionLoading(true);
    try {
      const res = await fetch(`${API}/api/kitchen/throttle/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(settingsForm)
      });
      
      if (res.ok) {
        const data = await res.json();
        setStatus(data.status);
        toast.success('Settings saved');
        setShowSettings(false);
        fetchEvents();
      } else {
        toast.error('Failed to save settings');
      }
    } catch (err) {
      toast.error('Error saving settings');
    } finally {
      setActionLoading(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString();
  };

  const getReasonLabel = (reason) => {
    const labels = {
      'manual': 'Manual',
      'pending_orders': 'Too Many Orders',
      'ticket_time': 'Slow Ticket Time',
      'print_queue': 'Print Queue Backlog',
      'busy_lunch_rush': 'Busy (Lunch Rush)'
    };
    return labels[reason] || reason;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  const isThrottled = status?.isThrottled;
  const metrics = status?.metrics || {};

  return (
    <div className="space-y-6" data-testid="order-throttle-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Order Throttling</h1>
          <p className="text-gray-500">Control online order flow based on kitchen capacity</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchStatus}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>
      </div>

      {/* Main Status Card */}
      <Card className={isThrottled ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'} data-testid="throttle-status-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`p-4 rounded-full ${isThrottled ? 'bg-red-100' : 'bg-green-100'}`}>
                {isThrottled ? (
                  <Pause className="h-8 w-8 text-red-600" />
                ) : (
                  <Play className="h-8 w-8 text-green-600" />
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold">
                  {isThrottled ? 'Online Orders PAUSED' : 'Accepting Online Orders'}
                </h2>
                {isThrottled && (
                  <p className="text-gray-600 mt-1">
                    Reason: <span className="font-medium">{getReasonLabel(status?.throttleReason)}</span>
                    {status?.triggeredBy && ` (${status.triggeredBy})`}
                  </p>
                )}
                {status?.throttledAt && (
                  <p className="text-sm text-gray-500">Since: {formatTime(status.throttledAt)}</p>
                )}
              </div>
            </div>
            <div>
              {isThrottled ? (
                <Button size="lg" onClick={handleResume} disabled={actionLoading} className="bg-green-600 hover:bg-green-700">
                  {actionLoading ? <RefreshCw className="h-5 w-5 mr-2 animate-spin" /> : <Play className="h-5 w-5 mr-2" />}
                  Resume Orders
                </Button>
              ) : (
                <Button size="lg" variant="destructive" onClick={handlePause} disabled={actionLoading}>
                  {actionLoading ? <RefreshCw className="h-5 w-5 mr-2 animate-spin" /> : <Pause className="h-5 w-5 mr-2" />}
                  Pause Orders
                </Button>
              )}
            </div>
          </div>

          {/* Customer Message Preview */}
          {isThrottled && status?.customerMessage && (
            <div className="mt-4 p-3 bg-white rounded-lg border">
              <p className="text-sm text-gray-500 mb-1">Customer sees:</p>
              <p className="font-medium">{status.customerMessage}</p>
              {status?.estimatedWaitMinutes && (
                <p className="text-sm text-gray-600 mt-1">
                  Estimated wait: ~{status.estimatedWaitMinutes} minutes
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="metric-pending-orders">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Orders</p>
                <p className="text-3xl font-bold">{metrics.pendingOrders || 0}</p>
              </div>
              <div className={`p-2 rounded-full ${
                metrics.pendingOrders > (status?.settings?.maxPendingOrders || 20) 
                  ? 'bg-red-100 text-red-600' 
                  : 'bg-blue-100 text-blue-600'
              }`}>
                <List className="h-6 w-6" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Threshold: {status?.settings?.maxPendingOrders || 20}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="metric-ticket-time">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg Ticket Time</p>
                <p className="text-3xl font-bold">{metrics.avgTicketTimeMinutes || 0}m</p>
              </div>
              <div className={`p-2 rounded-full ${
                metrics.avgTicketTimeMinutes > (status?.settings?.maxAvgTicketTimeMinutes || 15)
                  ? 'bg-red-100 text-red-600'
                  : 'bg-green-100 text-green-600'
              }`}>
                <Clock className="h-6 w-6" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Threshold: {status?.settings?.maxAvgTicketTimeMinutes || 15}m
            </p>
          </CardContent>
        </Card>

        <Card data-testid="metric-print-queue">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Print Queue</p>
                <p className="text-3xl font-bold">{metrics.printQueueSize || 0}</p>
              </div>
              <div className={`p-2 rounded-full ${
                metrics.printQueueSize > (status?.settings?.maxPrintQueueSize || 30)
                  ? 'bg-red-100 text-red-600'
                  : 'bg-purple-100 text-purple-600'
              }`}>
                <TrendingUp className="h-6 w-6" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Threshold: {status?.settings?.maxPrintQueueSize || 30}
            </p>
          </CardContent>
        </Card>

        <Card data-testid="metric-auto-throttle">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Auto-Throttle</p>
                <p className="text-xl font-bold">
                  {status?.settings?.autoThrottleEnabled ? 'Enabled' : 'Disabled'}
                </p>
              </div>
              <div className={`p-2 rounded-full ${
                status?.settings?.autoThrottleEnabled
                  ? 'bg-green-100 text-green-600'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                <Power className="h-6 w-6" />
              </div>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Cooldown: {status?.settings?.cooldownMinutes || 5}m
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Event History */}
      <Card data-testid="throttle-events-card">
        <CardHeader>
          <CardTitle>Throttle History</CardTitle>
          <CardDescription>Recent throttle/resume events</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {events.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Triggered By</TableHead>
                  <TableHead>Metrics</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="text-sm">
                      {formatTime(event.createdAt)}
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        event.eventType === 'throttled' 
                          ? 'bg-red-100 text-red-700' 
                          : event.eventType === 'resumed'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }>
                        {event.eventType}
                      </Badge>
                    </TableCell>
                    <TableCell>{getReasonLabel(event.reason) || '-'}</TableCell>
                    <TableCell className="capitalize">{event.triggeredBy || '-'}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {event.metrics?.pendingOrders !== undefined && (
                        <span>Orders: {event.metrics.pendingOrders}</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-10 text-gray-500">
              <Clock className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p>No throttle events yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Throttle Settings</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Auto Throttle Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Auto-Throttle</Label>
                <p className="text-sm text-gray-500">Automatically pause orders when thresholds are exceeded</p>
              </div>
              <Switch
                checked={settingsForm.autoThrottleEnabled}
                onCheckedChange={(v) => setSettingsForm({...settingsForm, autoThrottleEnabled: v})}
              />
            </div>

            {/* Pause Thresholds */}
            <div className="space-y-4">
              <h4 className="font-medium">Pause Thresholds</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Max Pending Orders</Label>
                  <Input
                    type="number"
                    value={settingsForm.maxPendingOrders || 20}
                    onChange={(e) => setSettingsForm({...settingsForm, maxPendingOrders: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Max Ticket Time (min)</Label>
                  <Input
                    type="number"
                    value={settingsForm.maxAvgTicketTimeMinutes || 15}
                    onChange={(e) => setSettingsForm({...settingsForm, maxAvgTicketTimeMinutes: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Max Print Queue</Label>
                  <Input
                    type="number"
                    value={settingsForm.maxPrintQueueSize || 30}
                    onChange={(e) => setSettingsForm({...settingsForm, maxPrintQueueSize: parseInt(e.target.value)})}
                  />
                </div>
              </div>
            </div>

            {/* Resume Thresholds */}
            <div className="space-y-4">
              <h4 className="font-medium">Resume Thresholds</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Resume Orders</Label>
                  <Input
                    type="number"
                    value={settingsForm.resumePendingOrders || 10}
                    onChange={(e) => setSettingsForm({...settingsForm, resumePendingOrders: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Resume Ticket Time</Label>
                  <Input
                    type="number"
                    value={settingsForm.resumeAvgTicketTimeMinutes || 10}
                    onChange={(e) => setSettingsForm({...settingsForm, resumeAvgTicketTimeMinutes: parseInt(e.target.value)})}
                  />
                </div>
                <div>
                  <Label>Resume Print Queue</Label>
                  <Input
                    type="number"
                    value={settingsForm.resumePrintQueueSize || 15}
                    onChange={(e) => setSettingsForm({...settingsForm, resumePrintQueueSize: parseInt(e.target.value)})}
                  />
                </div>
              </div>
            </div>

            {/* Cooldown */}
            <div>
              <Label>Cooldown (minutes)</Label>
              <Input
                type="number"
                value={settingsForm.cooldownMinutes || 5}
                onChange={(e) => setSettingsForm({...settingsForm, cooldownMinutes: parseInt(e.target.value)})}
                className="w-32"
              />
              <p className="text-sm text-gray-500 mt-1">Wait time before auto-resume</p>
            </div>

            {/* Customer Message */}
            <div>
              <Label>Customer Message</Label>
              <Textarea
                value={settingsForm.throttleMessage || ''}
                onChange={(e) => setSettingsForm({...settingsForm, throttleMessage: e.target.value})}
                placeholder="We're currently very busy..."
                rows={2}
              />
            </div>

            {/* Show Estimated Wait */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Show Estimated Wait</Label>
                <p className="text-sm text-gray-500">Display wait time to customers</p>
              </div>
              <Switch
                checked={settingsForm.showEstimatedWait}
                onCheckedChange={(v) => setSettingsForm({...settingsForm, showEstimatedWait: v})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button onClick={handleSaveSettings} disabled={actionLoading}>
              {actionLoading && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
